// Carries a share link's ?ref=<referrer id> across sign-in for visitors who
// arrive logged out. Sign-in sends it as `referralId` (the server credits the
// referrer only when the account is new), and the link visit is reported as
// share.inbound once they are logged in, so a stream join can earn invite.join.
import { shareAssetFromPath } from "./shareInbound";

const REFERRAL_KEY = "siki.pendingReferral";
const SHARE_INBOUND_KEY = "siki.pendingShareInbound";
const DAY_MS = 24 * 60 * 60 * 1000;
const REFERRAL_TTL_MS = 30 * DAY_MS;
// The server only credits a stream join within 24h of the link visit
// (skcore services/engagement/inviteJoins.js LINK_WINDOW_MS).
const SHARE_INBOUND_TTL_MS = DAY_MS;
const OBJECT_ID = /^[a-f\d]{24}$/i;
// Links the app knows how to open (skmob DynamicLinkService.handleDeepLink).
const APP_LINK_ORIGIN = "https://www.siki.io";
const APP_LINK_PATHS = {
  song: (id) => `/app/SongDetails/${id}`,
  profile: (id) => `/app/ArtistProfile/${id}`,
  playlist: (id) => `/app/Music/${id}`,
};

function readFresh(key, ttlMs, now) {
  try {
    const saved = JSON.parse(localStorage.getItem(key) || "null");
    if (saved && typeof saved.savedAt === "number" && now - saved.savedAt <= ttlMs) return saved;
    if (saved) localStorage.removeItem(key);
  } catch (e) {
    void e; // storage unavailable or corrupt: behave as if nothing was saved
  }
  return null;
}

function write(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    void e;
  }
}

function remove(key) {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    void e;
  }
}

/** Saves the referrer of a logged-out landing. Logged-in visitors are reported directly by useShareInbound. */
export function capturePendingReferral({ pathname, search }, viewerId, now = Date.now()) {
  if (viewerId) return;
  const params = new URLSearchParams(search || "");
  const ref = params.get("ref") || params.get("referralId");
  if (!ref || !OBJECT_ID.test(ref)) return;
  const asset = shareAssetFromPath(pathname);
  write(REFERRAL_KEY, { ...asset, ref, savedAt: now });
  if (asset) write(SHARE_INBOUND_KEY, { ...asset, ref, savedAt: now });
}

/** The sign-in payload with the saved referrer added as `referralId`. */
export function withPendingReferral(payload, now = Date.now()) {
  const saved = readFresh(REFERRAL_KEY, REFERRAL_TTL_MS, now);
  if (!saved || (payload && payload.referralId)) return payload;
  return { ...payload, referralId: saved.ref };
}

/**
 * { ref, link } for handing the share link to a fresh app install, or null.
 * `link` is an app-openable URL that always carries ?ref.
 */
export function pendingInstallHandoff(now = Date.now()) {
  const saved = readFresh(REFERRAL_KEY, REFERRAL_TTL_MS, now);
  if (!saved) return null;
  const ref = saved.ref;
  if (saved.assetType === "livestream") {
    return { ref, link: `${APP_LINK_ORIGIN}/Home?livestream=${saved.assetId}&ref=${ref}` };
  }
  const path = APP_LINK_PATHS[saved.assetType];
  return { ref, link: `${APP_LINK_ORIGIN}${path ? path(saved.assetId) : "/"}?ref=${ref}` };
}

export function clearPendingReferral() {
  remove(REFERRAL_KEY);
}

/** The share.inbound a logged-out landing could not report yet. Returns it once, then forgets it. */
export function takePendingShareInbound(viewerId, now = Date.now()) {
  const saved = readFresh(SHARE_INBOUND_KEY, SHARE_INBOUND_TTL_MS, now);
  if (!saved) return null;
  remove(SHARE_INBOUND_KEY);
  if (String(saved.ref) === String(viewerId)) return null;
  return { assetType: saved.assetType, assetId: saved.assetId, ref: saved.ref };
}
